<template>
  <FormModal :header="modalHeader" :name="name" @hide="handleHideModal">
    <FormModalBody
      footer-button-text="Save"
      :is-button-loading="form.processing"
      @submit="saveForm"
    >
      <FormAutocompleteSelect
        v-model="form.companyId"
        :static-options="
          work.company?.id
            ? [{ label: work.company.name, value: work.company.id }]
            : []
        "
        autocomplete-url="/companies"
        :disabled="isCustomCompany"
        :is-error="Boolean(form.errors.company_id)"
        :errors="form.errors.company_id"
        @input="form.clearErrors('company_id')"
      >
        <template #label>Company</template>
      </FormAutocompleteSelect>
      <FormCheckbox
        v-if="!work.company?.id"
        v-model="isCustomCompany"
        class="tw-mt-2"
      >
        Add company
      </FormCheckbox>
      <template v-if="isCustomCompany">
        <FormInput
          class="tw-mt-4"
          v-model="form.company.name"
          :is-error="Boolean(form.errors['company.name'])"
          :errors="form.errors['company.name']"
          @input="form.clearErrors('company.name')"
          type="text"
          is-required
          test_id="experience-company-name-input"
        >
          <template #label>Company name</template>
        </FormInput>
        <FormImageUpload
          wrapper-classes="tw-mt-4"
          :is-error="Boolean(logoErrors)"
          :errors="logoErrors"
          @uploaded="handleUploadedLogo"
          @errors="handleLogoErrors"
          :max-file-size-in-mb="1"
          :preview="work.company?.logoPath"
        >
          <template #upload-button-label>Upload logo</template>
        </FormImageUpload>
        <FormInput
          v-model="form.company.website"
          :is-error="Boolean(form.errors['company.website'])"
          :errors="form.errors['company.website']"
          @input="form.clearErrors('company.website')"
          class="tw-mt-4"
          type="text"
          test_id="experience-website-input"
          is-required
        >
          <template #label>Website</template>
        </FormInput>
        <FormSelectWithGroups
          v-model="form.company.industryList"
          :groups="$page.props.workIndustrySpecialties"
          :is-error="Boolean(form.errors['company.industry_list'])"
          :errors="form.errors['company.industry_list']"
          @update:model-value="form.clearErrors('company.industry_list')"
          multiple
          class="tw-mt-4"
        >
          <template #label>What industry?</template>
        </FormSelectWithGroups>
        <hr class="tw-my-4" />
      </template>
      <FormInput
        v-model="form.position"
        :is-error="Boolean(form.errors.position)"
        :errors="form.errors.position"
        @input="form.clearErrors('position')"
        type="text"
        is-required
        class="tw-mt-4"
        test_id="experience-position-input"
      >
        <template #label>Position</template>
      </FormInput>
      <FormInput
        v-model="form.description"
        :is-error="Boolean(form.errors.description)"
        :errors="form.errors.description"
        @input="form.clearErrors('description')"
        class="tw-mt-4"
        as="textarea"
        rows="4"
        test_id="experience-description-input"
      >
        <template #label>Description</template>
      </FormInput>
      <div class="tw-mt-4 tw-flex tw-items-end tw-space-x-4">
        <FormSelect
          v-model="form.fromMonth"
          :options="monthsOptions"
          :is-error="Boolean(form.errors.from)"
          @input="form.clearErrors('from')"
          wrapper-classes="tw-w-full md:tw-w-1/2"
          is-required
          placeholder="Month"
          test_id="experience-from-month-input"
        >
          <template #label>From</template>
        </FormSelect>
        <FormSelect
          v-model="form.fromYear"
          :options="yearsOptions"
          :is-error="Boolean(form.errors.from)"
          @input="form.clearErrors('from')"
          wrapper-classes="tw-w-full md:tw-w-1/2"
          placeholder="Year"
          test_id="experience-from-year-input"
        />
      </div>
      <p v-if="form.errors.from" class="tw-mt-2 tw-text-xs tw-text-error-500">
        {{ form.errors.from.join(', ') }}
      </p>
      <div class="tw-mt-4 tw-flex tw-items-end tw-space-x-4">
        <FormSelect
          v-model="form.toMonth"
          :options="monthsOptions"
          :is-error="Boolean(form.errors.to)"
          @input="form.clearErrors('to')"
          wrapper-classes="tw-w-full md:tw-w-1/2"
          is-required
          :disabled="form.toPresent"
          placeholder="Month"
        >
          <template #label>To</template>
        </FormSelect>
        <FormSelect
          v-model="form.toYear"
          :options="yearsOptions"
          :is-error="Boolean(form.errors.to)"
          @input="form.clearErrors('to')"
          wrapper-classes="tw-w-full md:tw-w-1/2"
          :disabled="form.toPresent"
          placeholder="Year"
        />
      </div>
      <p v-if="form.errors.to" class="tw-mt-2 tw-text-xs tw-text-error-500">
        {{ form.errors.to.join(', ') }}
      </p>
      <FormCheckbox
        v-model="form.toPresent"
        :is-error="Boolean(form.errors.to_present)"
        :errors="form.errors.to_present"
        @input="form.clearErrors('to_present', 'to')"
        class="tw-mt-4"
        test_id="form-checkbox"
      >
        Still work here
      </FormCheckbox>
    </FormModalBody>
  </FormModal>
</template>

<script setup>
import _capitalize from 'lodash/capitalize'
import FormModalBody from '@/shared/components/modals/FormModal/FormModalBody.vue'
import FormModal from '@/shared/components/modals/FormModal/FormModal.vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import FormCheckbox from '@/shared/components/forms/FormCheckbox.vue'
import FormSelect from '@/shared/components/forms/FormSelect.vue'
import { getYearsOptionsList } from '@/shared/utils/dates/yearsOptionsList.js'
import FormImageUpload from '@/shared/components/forms/FormImageUpload.vue'
import { useForm } from '@inertiajs/vue3'
import { monthsOptions } from '@/shared/utils/dates/monthsOptionsList.js'
import { computed, ref, watch } from 'vue'
import { useFormDates } from '@/shared/composables/useFormDates.js'
import { useModals } from '@/shared/composables/useModals.js'
import FormSelectWithGroups from '@/shared/components/forms/FormSelectWithGroups.vue'
import snakeCaseKeys from 'snakecase-keys'
import FormAutocompleteSelect from '@/shared/components/forms/FormAutocompleteSelect.vue'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  mode: {
    type: String,
    required: true,
    validator: function (value) {
      return ['EDIT', 'ADD'].includes(value.toUpperCase())
    },
  },
  work: {
    type: Object,
    default: () => ({
      companyId: null,
      company: null,
      position: '',
      description: '',
      from: null,
      to: null,
      toPresent: false,
    }),
  },
})

const { closeModal } = useModals()
const { formatFormDate, getMonthFromDateString, getYearFromDateString } =
  useFormDates()

const logoErrors = ref(null)
const isCustomCompany = ref(false)
const form = useForm({
  ...props.work,
  fromMonth: getMonthFromDateString(props.work.from),
  fromYear: getYearFromDateString(props.work.from),
  toMonth: getMonthFromDateString(props.work.to),
  toYear: getYearFromDateString(props.work.to),
})

watch(isCustomCompany, (checked) => {
  form.company = checked
    ? {
        name: '',
        logo: '',
        website: '',
        industryList: [],
      }
    : null
})

const modalHeader = computed(() => {
  return `${_capitalize(props.mode)} work experience`
})

const yearsOptions = computed(() => {
  return getYearsOptionsList()
})

const saveForm = () => {
  const method = props.mode === 'EDIT' ? 'patch' : 'post'
  const url =
    props.mode === 'EDIT'
      ? `/profile/mentor/work_experiences/${props.work.id}`
      : '/profile/mentor/work_experiences'

  form
    .transform((data) => {
      return {
        work_experience: {
          ...(props.mode === 'EDIT' || !isCustomCompany.value
            ? { company_id: data.companyId }
            : {}),
          ...(props.mode === 'ADD' && isCustomCompany.value
            ? {
                company: {
                  name: data.company.name,
                  logo: data.company.logo,
                  website: data.company.website,
                  industry_list: data.company.industryList,
                },
              }
            : {}),
          position: data.position,
          description: data.description,
          from:
            !data.fromMonth || !data.fromYear
              ? null
              : formatFormDate(data.fromYear, data.fromMonth),
          to:
            data.toPresent || !data.toMonth || !data.toYear
              ? null
              : formatFormDate(data.toYear, data.toMonth),
          to_present: data.toPresent,
        },
      }
    })
    [method](url, {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        closeModal(props.name)
      },
    })
}

const handleUploadedLogo = (file) => {
  logoErrors.value = null
  form.company.logo = file
}

const handleLogoErrors = (errors) => {
  logoErrors.value = errors
}

const handleHideModal = () => {
  if (props.mode === 'ADD') {
    form.reset()
    isCustomCompany.value = false
  }
}
</script>
